import { BarChart3 } from "lucide-react";
import {
  ColoredOutlineButton,
  OUTLINE_ICON_BUTTON_ICON_CLASS,
} from "@/components/ui/colored-outline-button";
import { AddToCalendarButton } from "./AddToCalendarButton";
import type { AuctionCalendarEvent } from "@/utils/calendarUtils";
import FeesAndDeliveryBlock from "./FeesAndDeliveryBlock";
import BidHistoryButton from "./BidHistoryButton";
import { DeliveryFeeZones } from "@/types/listing";

interface ListingQuickActionsProps {
  price: number;
  deliveryFee?: DeliveryFeeZones;
  pickupDisplayText?: string | null;
  hideWhenOwn?: boolean;
  calendarEvent?: AuctionCalendarEvent | null;
  /** Full stats dialog (admin / seller). */
  showStats?: boolean;
  onStatsClick?: () => void;
  statsLoading?: boolean;
  statsLabel?: string;
  /** Buyer-facing anonymous bid history (same style as fees/delivery). */
  showBidHistory?: boolean;
  listingId?: string;
  className?: string;
}

export function ListingQuickActions({
  price,
  deliveryFee,
  pickupDisplayText,
  hideWhenOwn = false,
  calendarEvent,
  showStats = false,
  onStatsClick,
  statsLoading = false,
  statsLabel = "Statistik",
  showBidHistory = false,
  listingId,
  className,
}: ListingQuickActionsProps) {
  const showFees = !hideWhenOwn;
  const hasCalendar = !!calendarEvent;
  const hasStats = showStats && !!onStatsClick;
  const hasBidHistory = showBidHistory && !!listingId && !hasStats;

  if (!hasCalendar && !hasStats && !hasBidHistory && !showFees) return null;

  return (
    <div className={className ?? "flex items-center gap-1.5 flex-shrink-0"}>
      {hasCalendar ? (
        <AddToCalendarButton event={calendarEvent!} />
      ) : null}
      {hasStats ? (
        <ColoredOutlineButton
          type="button"
          size="icon"
          variant="neutral"
          onClick={onStatsClick}
          disabled={statsLoading}
          isLoading={statsLoading}
          aria-label={statsLabel}
          title={statsLabel}
        >
          <BarChart3
            className={OUTLINE_ICON_BUTTON_ICON_CLASS}
            strokeWidth={2.35}
          />
        </ColoredOutlineButton>
      ) : null}
      {hasBidHistory ? <BidHistoryButton listingId={listingId!} /> : null}
      {showFees ? (
        <FeesAndDeliveryBlock
          price={price}
          deliveryFee={deliveryFee}
          pickupDisplayText={pickupDisplayText}
          hideWhenOwn={false}
        />
      ) : null}
    </div>
  );
}
